import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface RoleDto {
  id: string;
  name: string;
  normalizedName?: string;
  isSystemRole: boolean;
  permissionCount: number;
  permissions: string[];
}

export interface CreateRoleDto {
  name: string;
  permissionIds?: number[];
}

export interface UpdateRoleDto {
  name: string;
}

export interface AssignPermissionsDto {
  permissionIds: number[];
}

export interface RoleWithPermissionsDto {
  id: string;
  name: string;
  isSystemRole: boolean;
  permissions: PermissionDto[];
  permissionGroups: PermissionGroupDto[];
}

export interface PermissionDto {
  id: number;
  code: string;
  module: string;
  action: string;
  description?: string;
  isActive: boolean;
}

export interface PermissionGroupDto {
  module: string;
  moduleDisplayName: string;
  permissions: PermissionDto[];
}

@Injectable({
  providedIn: 'root'
})
export class RolesService {
  private readonly apiUrl = `${environment.apiUrl}/roles`;

  constructor(private http: HttpClient) {}

  /**
   * Obtiene todos los roles del sistema
   */
  getAllRoles(): Observable<RoleDto[]> {
    return this.http.get<RoleDto[]>(this.apiUrl);
  }

  /**
   * Obtiene un rol por su ID con sus permisos
   */
  getRoleById(id: string): Observable<RoleWithPermissionsDto> {
    return this.http.get<RoleWithPermissionsDto>(`${this.apiUrl}/${id}`);
  }

  /**
   * Crea un nuevo rol
   */
  createRole(role: CreateRoleDto): Observable<RoleDto> {
    return this.http.post<RoleDto>(this.apiUrl, role);
  }

  /**
   * Actualiza un rol existente
   */
  updateRole(id: string, role: UpdateRoleDto): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.apiUrl}/${id}`, role);
  }

  /**
   * Elimina un rol
   */
  deleteRole(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${this.apiUrl}/${id}`);
  }

  /**
   * Obtiene los permisos de un rol
   */
  getRolePermissions(roleId: string): Observable<PermissionDto[]> {
    return this.http.get<PermissionDto[]>(`${this.apiUrl}/${roleId}/permissions`);
  }

  /**
   * Asigna permisos a un rol
   */
  assignPermissions(roleId: string, permissionIds: number[]): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${roleId}/permissions`, { permissionIds });
  }
}

